import * as React from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';

import { HeadlineLevel, Margin, ParagraphSize, SITE } from 'features/ui/enums';
import {SITE_URLS} from 'features/search/constants';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {DisposalForm, ResidenceType} from 'features/search/filters/enums';
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {BuyResidenceObjectModel} from 'components/organisms/BuyResidenceGeneral/types';
import {getQueryAsObject} from 'components/organisms/BuyResidenceGeneral/services/helpers';
import {getBuyResidenceCookie} from 'components/organisms/BuyResidenceGeneral/services/api';
import {getCurrentSite, getLocalizedKvm, getLocalizedPrice, getLocalizedRok} from 'features/services/utils/env-helper';

import './_c2bObjectInfoCard.scss';

import {P} from 'components/atoms/Text/P';
import {Icon} from 'components/atoms/Media/Icon';
import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {AsyncImage} from 'components/atoms/Media/AsyncImage';
import {SiteSpecificContent} from 'components/atoms/SiteSpecificContent';
import {Headline} from '@atoms/Text/Headline';

interface Props {

    onStartPage?: boolean;
    currentObject: BuyResidenceObjectModel;
    objectVipKey?: string;
    noPaymentInfo?: boolean;
}

export const C2BObjectInfoCard: React.FunctionComponent<Props> = ({ onStartPage, currentObject, objectVipKey, noPaymentInfo}) => {
    const renderTitle = () => {
        if (getCurrentSite() === SITE.FI && typeof currentObject.jmoyNumberOfRoomsExtraInfo !== 'undefined' && currentObject.jmoyNumberOfRoomsExtraInfo !== null) {
            return `${currentObject.jmoyNumberOfRoomsExtraInfo}, ${getLocalizedKvm(currentObject.livingArea)}`;
        }
        if (getCurrentSite() === SITE.NO && currentObject.jmnoBraInternal) {
            return `${getLocalizedRok(currentObject.numberOfRooms)}, ${getLocalizedKvm(currentObject.jmnoBraInternal)}`;
        }

        return `${getLocalizedRok(currentObject.numberOfRooms)}, ${getLocalizedKvm(currentObject.livingArea)}`;
    };

    const _renderFloor = () => {
        if(
            currentObject.residenceTypeGuid === ResidenceType.DETACHED_HOUSE ||
            currentObject.residenceTypeGuid === ResidenceType.HOUSE ||
            currentObject.residenceTypeGuid === ResidenceType.TOWN_HOUSE ||
            currentObject.residenceTypeGuid === ResidenceType.SEMI_DETACHED_HOUSE) return false;
        if(typeof currentObject.floor === 'undefined' || currentObject.floor === null) return false;
        if(typeof currentObject.maxFloor === 'undefined' || currentObject.maxFloor === null) return false;

        return (
            <li>
                <span><FormattedMessage id="jm.components.ClickToBuy.objectInfo.floorHeadline" /></span>
                <span>{currentObject.floor} / {currentObject.maxFloor}</span>
            </li>
        );
    };

    const renderInhabitData = () => {
        if(!currentObject.inhabitDate) return null;

        return (
            <li>
                <span><FormattedMessage id="jm.components.ClickToBuy.objectInfo.inhabitDateHeadline" /></span>
                <span>{renderInhabitDate(currentObject.inhabitDate, currentObject.inhabitDateEnd, currentObject.inhabitDateDetermined)}</span>
            </li>
        );
    };

    const renderFee = () => {
        if(!currentObject.objectFee) return null;

        return (
            <>
                { currentObject.disposalFormGuid.toString() !== DisposalForm.OWNERSHIP &&
                <li>
                    <span><FormattedMessage id="jm.components.ResidenceExplorer.fee" /></span>
                    <span>
                        {getLocalizedPrice(currentObject.objectFee)}
                    </span>
                </li>
                }


                { currentObject.disposalFormGuid.toString() === DisposalForm.OWNERSHIP &&
                    <SiteSpecificContent notFinland>
                        <li>
                            <span><FormattedMessage id="jm.components.ResidenceExplorer.communityFee" /></span>
                            <span>
                                {getLocalizedPrice(currentObject.sharedCost)}
                            </span>
                        </li>
                    </SiteSpecificContent>
                }

            </>

        )
    };

    const getBuyUrl = () => {

        let url: string;

        const parameters = getQueryAsObject();
        const bookingId = parameters !== null && parameters.hasOwnProperty('bookingId') ? parameters['bookingId'] : null;

        if(typeof objectVipKey === 'undefined' || objectVipKey === null) {
            url = `${SITE_URLS.clickToBuy}${currentObject.objectId}/`;

            if(bookingId) {
                url += '?bookingId=' + bookingId;
            }
        } else {
            url = `${SITE_URLS.clickToBuy}${currentObject.objectId}/${objectVipKey}`;
        }

        return url;
    };

    const checkBookableStatus = async () => {
        const jsonModel = await getBuyResidenceCookie(); // CookieStore.getClickToBuyModel(currentObject.objectId);

        if(jsonModel && jsonModel.objectId === currentObject.objectId) {
            return jsonModel.objectId === currentObject.objectId;
        }

        return false;
    };

    const getJmnoPrice = () => {
        if(currentObject.jmnoFellesgjeld && currentObject.residenceTypeGuid === ResidenceType.APARTMENT && (currentObject.disposalFormGuid === DisposalForm.OWNERSHIP || currentObject.disposalFormGuid === DisposalForm.OWNERSHIP_APARTMENT)){
            return getLocalizedPrice(currentObject.objectPrice + currentObject.jmnoFellesgjeld + currentObject.otherExpenses);
        }

        return getLocalizedPrice(currentObject.objectPrice + currentObject.otherExpenses);

    };

    const contentClasses = classnames('c2b-object-info-card__content', {
        'c2b-object-info-card__content--no-payment-info': noPaymentInfo,
    });

    const currentSite = getCurrentSite();

    return (
        <div className="c2b-object-info-card">
            <div className="c2b-object-info-card__image">
                <AsyncImage
                    image={currentObject.floorPlanImage}
                    quality={ImageSizes.SIZE_600}
                    fullWidth
                    noCrop
                />
            </div>

            <div className={contentClasses}>
                <P margin={Margin.S} size={ParagraphSize.S}>
                    {currentObject.municipality} / {currentObject.area} / {currentObject.stageName}
                </P>

                <Headline level={HeadlineLevel.H3} margin={Margin.L}>{renderTitle()}</Headline>
                <ul className="c2b-object-info-card__prices">
                    <li>
                        <span><FormattedMessage id="jm.components.QuickFacts.objectNumber" /></span>
                        <span>{currentObject.objectNumber}</span>
                    </li>

                    <SiteSpecificContent sites={[SITE.NO]}>
                        <li>
                            <span><FormattedMessage id="jm.components.QuickFacts.totalPrice" /></span>
                            <span>{getJmnoPrice()}</span>
                        </li>
                    </SiteSpecificContent>

                    <SiteSpecificContent sites={[SITE.FI]}>
                        <li>
                            <span><FormattedMessage id="jm.components.QuickFacts.totalPrice" /></span>
                            <span>{getLocalizedPrice(currentObject.objectPrice + currentObject.jmoyLoanPrice)}</span>

                            <ul>
                                <li>
                                    <span><FormattedMessage id="jm.components.QuickFacts.price" /></span>
                                    <span>{getLocalizedPrice(currentObject.objectPrice)}</span>
                                </li>
                                <li>
                                    <span><FormattedMessage id="jm.components.Pricelist.communityFee" /></span>
                                    <span>{getLocalizedPrice(currentObject.jmoyLoanPrice)}</span>
                                </li>
                            </ul>
                        </li>
                    </SiteSpecificContent>

                    <SiteSpecificContent notFinland>
                        <li>
                            <span><FormattedMessage id="jm.components.QuickFacts.price" /></span>
                            <span>{getLocalizedPrice(currentObject.objectPrice)}</span>
                        </li>
                    </SiteSpecificContent>

                    <SiteSpecificContent showOnNorway>
                        {currentObject.jmnoFellesgjeld && currentObject.residenceTypeGuid === ResidenceType.APARTMENT && (currentObject.disposalFormGuid === DisposalForm.OWNERSHIP || currentObject.disposalFormGuid === DisposalForm.OWNERSHIP_APARTMENT) &&
                            <li>
                                <span><FormattedMessage id="jm.components.QuickFacts.fellesgjeldOwnership" /></span>
                                <span>{getLocalizedPrice(currentObject.jmnoFellesgjeld)}</span>
                            </li>
                        }

                        {currentObject.otherExpenses &&
                            <li>
                                <span><FormattedMessage id="jm.components.QuickFacts.omkostninger" /></span>
                                 <span>{getLocalizedPrice(currentObject.otherExpenses)}</span>
                            </li>
                        }
                    </SiteSpecificContent>

                    {/*renderFloor()*/}
                    {renderFee()}
                    {renderInhabitData()}
                </ul>
            </div>

            <SiteSpecificContent showOnNorway>
                {onStartPage &&
                <div className="c2b-object-info-card__content">
                    <P />
                    <Button 
                        href={getBuyUrl()} 
                        buttonState={!currentObject.isBookable && !checkBookableStatus()
                            ? ButtonState.INACTIVE
                            : ButtonState.DEFAULT
                        } 
                        buttonColor={ButtonColor.PURCHASE} 
                        buttonSize={ButtonSize.WIDE} 
                        buttonType={ButtonType.DEFAULT} 
                    >
                        <FormattedMessage id='jm.components.ClickToBuyBanner.bookThis' />
                    </Button>
                </div>
                }
            </SiteSpecificContent>

            {!noPaymentInfo &&
                <React.Fragment>
                    <div className="c2b-object-info-card__booking-fee">
                        <ul className="c2b-object-info-card__prices">
                            <li>
                                <strong><FormattedMessage id='jm.components.ClickToBuy.objectInfo.bookingFee' /></strong>
                                <strong>{getLocalizedPrice(currentObject.bookingFee)}</strong>
                                <ul>
                                    <li>
                                        <FormattedMessage id='jm.components.c2bObjectInfoCard.bookingFeeInfo' />
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                    <div className="c2b-object-info-card__content">
                        {onStartPage &&
                            <Button 
                                href={getBuyUrl()} 
                                buttonState={!currentObject.isBookable && !checkBookableStatus()
                                    ? ButtonState.INACTIVE
                                    : ButtonState.DEFAULT
                                } 
                                buttonColor={(currentSite === SITE.SENIORGARDEN || currentSite === SITE.BORATT) ? ButtonColor.CTA : ButtonColor.PURCHASE} 
                                buttonSize={ButtonSize.WIDE} 
                                buttonType={ButtonType.DEFAULT} 
                            >
                                <FormattedMessage id='jm.components.ClickToBuyBanner.bookThis' />
                            </Button>
                        }
                        <P className="c2b-object-info-card__payment-info" center>
                            <SiteSpecificContent showOnSweden>
                                <Icon name="payment-swish" sizeS />
                            </SiteSpecificContent>
                            <SiteSpecificContent notFinland>
                                <Icon name="payment-visa" sizeL />
                                <Icon name="payment-mastercard" sizeL />
                            </SiteSpecificContent>
                            <SiteSpecificContent showOnFinland>
                                <Icon name="payment-nordea" sizeS noStroke />
                            </SiteSpecificContent>
                        </P>

                    </div>
                </React.Fragment>
            }
        </div>
    );
};

